import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Eye, EyeOff, KeyRound, Loader2, Mail, Smartphone, ShieldCheck, ShieldOff,
} from 'lucide-react';
import useTwoFactorSettings from '../../hooks/useTwoFactorSettings';
import TwoFactorChannelPicker from '../../components/TwoFactorChannelPicker';
import TwoFactorSetup from '../../components/TwoFactorSetup';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import client from '../../lib/api';
import { accountHook } from '../../lib/domHooks';

const Security = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { toast } = useToast();
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwords, setPasswords] = useState({
    current: '',
    next: '',
    repeat: '',
  });
  const {
    status, error, pending, code, setCode, busy, request, confirm, cancel,
    channel, setChannel,
  } = useTwoFactorSettings();

  if (error) {
    return (
      <div className="pusu-panel p-6">
        <p className="text-sm text-red-400">{error}</p>
      </div>
    );
  }
  if (!status) {
    return (
      <div className="pusu-panel p-6 flex items-center gap-2 text-sm text-gray-400">
        <Loader2 size={16} className="animate-spin" /> Güvenlik ayarları yükleniyor...
      </div>
    );
  }

  const enabled = status.enabled;

  const changePassword = async (event) => {
    event.preventDefault();
    if (passwords.next.length < 8) {
      toast({ title: 'Şifre çok kısa', description: 'Yeni şifre en az 8 karakter olmalıdır.' });
      return;
    }
    if (passwords.next !== passwords.repeat) {
      toast({ title: 'Şifreler eşleşmiyor', description: 'Yeni şifre alanlarını kontrol edin.' });
      return;
    }
    if (passwords.current === passwords.next) {
      toast({ title: 'Yeni şifre farklı olmalı' });
      return;
    }
    setPasswordBusy(true);
    try {
      await client.post('/auth/password/change', {
        current_password: passwords.current,
        new_password: passwords.next,
      });
      setPasswords({ current: '', next: '', repeat: '' });
      toast({
        title: 'Şifreniz değiştirildi',
        description: 'Güvenliğiniz için yeniden giriş yapmanız gerekiyor.',
      });
      try { await logout(); } catch { /* yeni şifre eski oturumu kapattı */ }
      navigate('/');
    } catch (requestError) {
      toast({
        title: 'Şifre değiştirilemedi',
        description: requestError.response?.data?.detail || 'Bir hata oluştu',
      });
    } finally {
      setPasswordBusy(false);
    }
  };

  return (
    <div className="pusu-panel p-6" {...accountHook('panel')}>
      <div className="mb-5" {...accountHook('page-header')}>
        <h2 className="text-xl font-bold gold-text" {...accountHook('title')}>Güvenlik</h2>
        <p className="text-sm text-gray-400" {...accountHook('subtitle')}>Hesabınızın giriş güvenliğini yönetin</p>
      </div>

      <div className="account-inset rounded-xl p-4 md:p-5" {...accountHook('two-factor')}>
        <div className="flex flex-wrap items-start gap-3">
          <div className={`${enabled ? 'account-icon-tile' : 'account-icon-off'} w-11 h-11 rounded-lg flex items-center justify-center shrink-0`}>
            {enabled ? <ShieldCheck size={20} /> : <ShieldOff size={20} />}
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-semibold">İki Adımlı Doğrulama</div>
            <p className="text-xs text-gray-400 mt-1">
              İsteğe bağlıdır. Açtığınızda her girişte Google Authenticator,
              SMS veya e-posta ile 6 haneli doğrulama kodu istenir.
            </p>
            <div className="mt-2 text-xs flex items-center gap-1.5 text-gray-400">
              {status.delivery_channel === 'totp'
                ? <KeyRound size={13} className="text-[var(--pusu-gold)]" />
                : status.delivery_channel === 'sms'
                ? <Smartphone size={13} className="text-[var(--pusu-gold)]" />
                : <Mail size={13} className="text-[var(--pusu-gold)]" />}
              {status.delivery_target || 'Doğrulama kanalı tanımlı değil'}
            </div>
          </div>
          <span
            className={`${enabled ? 'account-icon-tile' : 'account-icon-off'} px-2.5 py-1 rounded text-xs font-semibold`}
            data-bhs-status={enabled ? 'on' : 'off'}
            {...accountHook('badge')}
          >
            {enabled ? 'Aktif' : 'Kapalı'}
          </span>
        </div>

        {!status.can_enable && !enabled && (
          <p className="text-xs text-amber-400 mt-4" {...accountHook('notice')}>
            Şu anda kullanılabilir doğrulama yöntemi bulunmuyor. İletişim bilgilerinizi{' '}
            <Link to="/hesap/profil" className="underline">profilinizden</Link> kontrol edebilirsiniz.
          </p>
        )}

        {!enabled && !pending && (
          <TwoFactorChannelPicker
            channels={status.channels}
            value={channel}
            onChange={setChannel}
            disabled={busy}
            accent="var(--pusu-gold)"
            muted="var(--pusu-muted)"
          />
        )}
        {enabled && !pending && (
          <p className="text-xs text-gray-400 mt-4">
            Kanalı değiştirmek için doğrulamayı kapatıp yeniden açın.
          </p>
        )}

        {pending ? (
          <form className="mt-5 space-y-3" {...accountHook('form')} onSubmit={(event) => { event.preventDefault(); confirm(); }}>
            <TwoFactorSetup key={pending.challenge} pending={pending} />
            <input
              aria-label="Doğrulama kodu"
              required
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              minLength={status.code_length || 6}
              maxLength={status.code_length || 6}
              pattern="[0-9]{6}"
              className="pusu-input text-center text-2xl tracking-[0.5em] font-bold"
              {...accountHook('code')}
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
            <div className="flex flex-wrap gap-2" {...accountHook('actions')}>
              <button
                type="submit"
                {...accountHook('submit')}
                disabled={busy || code.length !== (status.code_length || 6)}
                className="gold-btn px-5 py-2.5 rounded-xl flex items-center gap-2 disabled:opacity-50"
              >
                {busy && <Loader2 size={14} className="animate-spin" />}
                {pending.action === 'enable' ? 'Doğrula ve Aç' : 'Doğrula ve Kapat'}
              </button>
              {pending.deliveryChannel !== 'totp' && <button
                type="button"
                disabled={busy}
                onClick={() => request(pending.action)}
                className="gold-outline-btn px-5 py-2.5 rounded-xl disabled:opacity-50"
              >
                Kodu tekrar gönder
              </button>}
              <button type="button" disabled={busy} onClick={cancel} className="px-5 py-2.5 rounded-xl text-sm text-gray-400 hover:text-white disabled:opacity-50">
                Vazgeç
              </button>
            </div>
          </form>
        ) : (
          <div className="mt-5" {...accountHook('actions')}>
            <button
              type="button"
              {...accountHook('submit')}
              disabled={busy || (!enabled && (!status.can_enable || !channel))}
              onClick={() => request(enabled ? 'disable' : 'enable')}
              className={`${enabled ? 'gold-outline-btn' : 'gold-btn'} px-5 py-2.5 rounded-xl flex items-center gap-2 disabled:opacity-50`}
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              {enabled ? 'Doğrulamayı Kapat' : channel === 'totp' ? 'Google Authenticator Kurulumunu Başlat' : 'Doğrulamayı Aç'}
            </button>
          </div>
        )}
      </div>

      <form
        onSubmit={changePassword}
        className="account-inset rounded-xl p-4 md:p-5 mt-4"
        {...accountHook('form')}
      >
        <div className="flex items-start gap-3 mb-5" {...accountHook('header')}>
          <div className="account-icon-tile w-11 h-11 rounded-lg flex items-center justify-center shrink-0">
            <KeyRound size={20} />
          </div>
          <div>
            <div className="font-semibold">Şifre Değiştir</div>
            <p className="text-xs text-gray-400 mt-1">
              Şifrenizi değiştirdiğinizde güvenliğiniz için tüm açık oturumlar kapatılır.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div {...accountHook('field', 'current_password')}>
            <label className="block text-xs text-gray-400 mb-1.5">Mevcut şifre</label>
            <input
              required
              type={showPasswords ? 'text' : 'password'}
              autoComplete="current-password"
              className="pusu-input"
              value={passwords.current}
              onChange={(event) => setPasswords({ ...passwords, current: event.target.value })}
            />
          </div>
          <div {...accountHook('field', 'new_password')}>
            <label className="block text-xs text-gray-400 mb-1.5">Yeni şifre</label>
            <input
              required
              minLength={8}
              type={showPasswords ? 'text' : 'password'}
              autoComplete="new-password"
              className="pusu-input"
              value={passwords.next}
              onChange={(event) => setPasswords({ ...passwords, next: event.target.value })}
            />
          </div>
          <div {...accountHook('field', 'new_password_repeat')}>
            <label className="block text-xs text-gray-400 mb-1.5">Yeni şifre tekrar</label>
            <input
              required
              minLength={8}
              type={showPasswords ? 'text' : 'password'}
              autoComplete="new-password"
              className="pusu-input"
              value={passwords.repeat}
              onChange={(event) => setPasswords({ ...passwords, repeat: event.target.value })}
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3" {...accountHook('actions')}>
          <button
            type="submit"
            {...accountHook('submit')}
            disabled={passwordBusy}
            className="gold-btn px-5 py-2.5 rounded-xl flex items-center gap-2 disabled:opacity-50"
          >
            {passwordBusy && <Loader2 size={14} className="animate-spin" />}
            Şifremi Değiştir
          </button>
          <button
            type="button"
            onClick={() => setShowPasswords((visible) => !visible)}
            className="gold-outline-btn px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm"
          >
            {showPasswords ? <EyeOff size={15} /> : <Eye size={15} />}
            {showPasswords ? 'Şifreleri gizle' : 'Şifreleri göster'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Security;
